import { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function VerifyEmailPage() {
	const { t } = useTranslation();
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const { user, refreshUser } = useAuth();
	const [status, setStatus] = useState('loading'); // loading | success | error
	const [message, setMessage] = useState('');

	useEffect(() => {
		const token = searchParams.get('token');
		if (!token) {
			setStatus('error');
			setMessage(t('verify.missing_token'));
			return;
		}
		authAPI
			.verifyEmail(token)
			.then((res) => {
				setStatus('success');
				setMessage(res.data?.email || '');
				if (user) refreshUser?.();
			})
			.catch((e) => {
				setStatus('error');
				setMessage(e?.response?.data?.detail || t('verify.failed'));
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchParams]);

	return (
		<div className="pt-24 min-h-screen flex items-center justify-center bg-[#F9F7F2] px-4" data-testid="verify-email-page">
			<div className="bg-white border border-[#E6E2D6] rounded-sm p-10 max-w-md w-full text-center">
				{status === 'loading' && (
					<>
						<Loader2 className="w-10 h-10 text-[#1A3C34] animate-spin mx-auto" />
						<h1 className="mt-4 text-2xl font-medium text-[#1A3C34] font-['Playfair_Display']">{t('verify.loading_title')}</h1>
						<p className="mt-2 text-sm text-[#666055]">{t('verify.loading_help')}</p>
					</>
				)}
				{status === 'success' && (
					<>
						<CheckCircle2 className="w-12 h-12 text-[#1A3C34] mx-auto" data-testid="verify-success-icon" />
						<h1 className="mt-4 text-2xl font-medium text-[#1A3C34] font-['Playfair_Display']">{t('verify.success_title')}</h1>
						<p className="mt-2 text-sm text-[#666055]">{t('verify.success_help')}</p>
						{message && <p className="mt-1 text-xs text-[#666055]">{message}</p>}
						<Button
							onClick={() => navigate(user ? '/dashboard' : '/auth')}
							className="mt-6 bg-[#1A3C34] hover:bg-[#153029] text-white rounded-sm h-11 px-8"
							data-testid="verify-continue-btn"
						>
							{user ? t('verify.go_dashboard') : t('verify.go_login')}
						</Button>
					</>
				)}
				{status === 'error' && (
					<>
						<XCircle className="w-12 h-12 text-[#9B2C2C] mx-auto" data-testid="verify-error-icon" />
						<h1 className="mt-4 text-2xl font-medium text-[#1A3C34] font-['Playfair_Display']">{t('verify.error_title')}</h1>
						<p className="mt-2 text-sm text-[#666055]">{message}</p>
						<Link
							to={user ? '/dashboard' : '/auth'}
							className="inline-block mt-6 bg-[#8B4513] hover:bg-[#6F3610] text-white rounded-sm h-11 px-8 leading-[44px] text-sm font-medium"
							data-testid="verify-error-cta"
						>
							{user ? t('verify.go_dashboard') : t('verify.go_login')}
						</Link>
					</>
				)}
			</div>
		</div>
	);
}